import { addDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";

/**
 * Parse a dd/mm/yyyy or dd-mm-yyyy string into a Date (midnight local). Returns
 * null if the input isn't a recognisable date.
 */
const parseDmy = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const m = raw.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!m) return null;
  const dt = new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10));
  dt.setHours(0, 0, 0, 0);
  return isNaN(dt.getTime()) ? null : dt;
};

const formatDmy = (dt) =>
  `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;

/**
 * Pick the soonest upcoming hearing date from NGT listingHistory.
 * Returns dd/mm/yyyy, or empty string if no future date exists.
 *
 * NGT details don't expose a dedicated "next hearing date" field, so we derive
 * it from the listing history — taking the earliest date today or later.
 */
export const pickNextHearingDate = (listingHistory) => {
  if (!Array.isArray(listingHistory) || listingHistory.length === 0) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const future = listingHistory
    .map((h) => parseDmy(h?.dateOfListing))
    .filter((dt) => dt && dt >= today)
    .sort((a, b) => a - b);
  return future.length === 0 ? "" : formatDmy(future[0]);
};

/**
 * Fetch the lawyer's mobileWithCountryCode for use as the SMS recipient on
 * reminder events. Returns empty string on miss or error (reminders still work
 * without it — just no SMS).
 */
export const fetchUserMobile = async (uid) => {
  if (!uid) return "";
  try {
    const snap = await getDocs(query(collection(db, "lawyers"), where("uid", "==", uid)));
    if (snap.empty) return "";
    return snap.docs[0].data()?.mobileWithCountryCode || "";
  } catch (err) {
    console.warn("fetchUserMobile error:", err);
    return "";
  }
};

/**
 * Create hearing-reminder events for a freshly exported case — mirrors the
 * High Court implementation:
 *   - 8:00 AM IST on the next hearing date: visible reminder
 *   - 6:00 PM IST on the next hearing date: hidden update-next-hearing prompt
 * Both events also write into `eventReminders` for the scheduler to pick up.
 *
 * No-ops if nextHearingDate is missing, malformed, or already in the past.
 */
export const scheduleHearingReminders = async ({
  user,
  caseRef,
  caseNo,
  partyName,
  adverseParty,
  nextHearingDate,
  userMobile,
  caseStatus,
}) => {
  if (!nextHearingDate || !user || !caseRef) return;

  try {
    // dd/mm/yyyy -> yyyy-mm-dd for the Date constructor
    const eventDateStr = nextHearingDate.split("/").reverse().join("-");
    const eventDate = new Date(eventDateStr);
    if (isNaN(eventDate.getTime())) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const hearingDay = new Date(eventDate);
    hearingDay.setHours(0, 0, 0, 0);
    if (hearingDay < today) return;

    const reminderDate = new Date(eventDate);
    reminderDate.setHours(8, 0, 0, 0); // 8:00 AM IST — visible reminder

    const updateTime = new Date(eventDate);
    updateTime.setHours(18, 0, 0, 0); // 6:00 PM IST — hidden update prompt

    const now = new Date();
    const dateOnly = (d) => d.toISOString().split("T")[0];

    if (reminderDate > now) {
      const reminderEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), {
        "Case No": caseNo,
        date: dateOnly(reminderDate),
        id: Math.floor(Math.random() * 90000) + 10000,
        mobile: userMobile || null,
        time: "08:00 AM",
        time1: reminderDate.getTime(),
        title: `${partyName} VS ${adverseParty}`,
        description: `Hearing Reminder : Case No. - ${caseNo}`,
        isScheduled: true,
        status: caseStatus,
        eventType: "reminder",
      });
      await addDoc(collection(db, "eventReminders"), {
        eventId: reminderEventRef.id,
        reminderTime: reminderDate,
        eventTitle: `Hearing Reminder : Case No. - ${caseNo}`,
        caseNo,
        caseId: caseRef.id,
        collection: caseStatus,
        status: "pending",
        createdAt: new Date(),
        scheduledBy: user.uid,
        recipientId: user.uid,
      });
    }

    if (updateTime > now) {
      const mainEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), {
        "Case No": caseNo,
        date: dateOnly(eventDate),
        id: Math.floor(Math.random() * 90000) + 10000,
        mobile: userMobile || null,
        time: "06:00 PM",
        time1: updateTime.getTime(),
        title: `Update Next Hearing: ${partyName} VS ${adverseParty}`,
        description: `Case No. - ${caseNo}`,
        isScheduled: true,
        status: caseStatus,
        eventType: "updateReminder",
        isHidden: true,
      });
      await addDoc(collection(db, "eventReminders"), {
        eventId: mainEventRef.id,
        reminderTime: updateTime,
        eventTitle: `Update Next Hearing : ${caseNo}`,
        caseNo,
        caseId: caseRef.id,
        collection: caseStatus,
        status: "pending",
        createdAt: new Date(),
        scheduledBy: user.uid,
        recipientId: user.uid,
      });
    }
  } catch (err) {
    // Don't let reminder failures break the export itself
    console.error("NGT reminder scheduling error:", err);
  }
};
